/**
 * Shared API request context.
 *
 * Bridges the React-tier organization context (URL slug → currentOrg) and the
 * plain-module fetch wrappers (organizationApi, directorApi, videoApi,
 * sellerFlowApi). The portal scopes requests by sending the active org id as a
 * header on JSON requests, and by embedding it in the URL path for native
 * media elements that cannot send custom headers.
 *
 * Lifecycle: OrgLayout / OrganizationContext call setActiveOrganizationId
 * whenever the user-facing org changes. Fetch wrappers read the value at
 * request time, so no listener wiring is required.
 */

export const ORG_HEADER = 'X-Cornercast-Organization-Id';

let activeOrganizationId = null;

export function setActiveOrganizationId(orgId) {
  activeOrganizationId = orgId || null;
}

export function getActiveOrganizationId() {
  return activeOrganizationId;
}

export function clearActiveOrganizationId() {
  activeOrganizationId = null;
}

/**
 * Return a headers object with the active organization header attached.
 *
 * @param {Record<string, string>} headers - Base headers to merge into.
 * @param {Object} [options]
 * @param {boolean} [options.includeOrg=true] - Set false for endpoints that
 *   must not be org-scoped (public proposal share links, the top-level
 *   organizations list before any org is selected, etc.). Passing false is
 *   only meaningful as documentation — the header is also omitted whenever no
 *   active org is set.
 * @returns {Record<string, string>}
 */
export function withOrgHeader(headers = {}, { includeOrg = true } = {}) {
  if (!includeOrg) return headers;
  const orgId = getActiveOrganizationId();
  if (!orgId) return headers;
  return { ...headers, [ORG_HEADER]: orgId };
}

/**
 * Build an organization-scoped path for video media endpoints.
 *
 * The backend serves native media (video asset, thumbnail, scene frames) under
 * /api/v1/organizations/{org_id}/videos/{job_id}/... so the org scope travels
 * in the URL — native <video>/<img> cannot send custom headers.
 *
 * If no active org is set we fall back to the legacy path and log a warning;
 * the resulting URL will almost certainly 400 when served to a <video>/<img>
 * tag, but failing loudly with a logged URL is more debuggable than silently
 * returning null.
 *
 * @param {string} jobId
 * @param {string} suffix - Path tail starting with '/' (e.g. '/asset',
 *   '/thumbnail', '/scenes/3/initial').
 */
export function buildOrgScopedVideoPath(jobId, suffix) {
  const orgId = getActiveOrganizationId();
  if (!orgId) {
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(
        `[apiContext] No active organization while building media URL for job ${jobId}; ` +
        'falling back to legacy /api/v1/videos path. Native media will likely fail.',
      );
    }
    return `/api/v1/videos/${jobId}${suffix}`;
  }
  return `/api/v1/organizations/${orgId}/videos/${jobId}${suffix}`;
}
